import type { KeyboardEvent } from 'react';
import React, { useCallback } from 'react';
import FocusLock from 'react-focus-lock';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { Conversation } from 'unified-help-center-alpha/src/components/conversation/Conversation';
import { useConversation } from 'unified-help-center-alpha/src/store/converse';
import { SEARCH_BAR_HEIGHT } from 'view/search/common';
import Blanket from '@atlaskit/blanket';
import FadeIn from '@atlaskit/motion/fade-in';
import Portal from '@atlaskit/portal';
import { Box, xcss } from '@atlaskit/primitives';
import { layers } from '@atlaskit/theme/constants';
import { token } from '@atlaskit/tokens';
import { ScrollLock } from '@atlassian/help-center-common-component/scroll-lock';
import { ConversationNavBar } from '../conversation-nav-bar';
import type { conversationModalQuery } from './__generated__/conversationModalQuery.graphql';

const ConversationModal = () => {
    const data = useLazyLoadQuery<conversationModalQuery>(
        graphql`
            query conversationModalQuery {
                me @required(action: THROW) {
                    user @required(action: THROW) {
                        ...ConversationUser
                        ...ConversationInputUser
                    }
                }
            }
        `,
        {}
    );
    const [conversation, { clearConversation }] = useConversation();

    const onKeyUp = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                // TODO: fix this
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                clearConversation();
            }
        },
        [clearConversation]
    );

    return (
        <FadeIn duration={200} entranceDirection="bottom">
            {() => (
                <Portal zIndex={layers.modal()}>
                    <Box xcss={conversationModalStyles}>
                        {/*Disabling existing violations, should be fixed when revisited.*/}
                        {/*eslint-disable-next-line jsx-a11y/no-autofocus*/}
                        <FocusLock autoFocus>
                            {/* Ensures scroll events are blocked on the document body and locked */}
                            <ScrollLock />
                            <Blanket shouldAllowClickThrough={false} isTinted>
                                <Box
                                    xcss={xcss({
                                        height: `calc(100% - ${SEARCH_BAR_HEIGHT})`,
                                    })}
                                    onKeyUp={onKeyUp}
                                >
                                    <ConversationNavBar />
                                    <Box xcss={conversationBoxStyles}>
                                        <Box
                                            xcss={xcss({
                                                width: '90%',
                                                pointerEvents: 'auto',
                                            })}
                                        >
                                            {conversation && <Conversation key="CONVERSATION" userRef={data.me.user} />}
                                        </Box>
                                    </Box>
                                </Box>
                            </Blanket>
                        </FocusLock>
                    </Box>
                </Portal>
            )}
        </FadeIn>
    );
};

export default ConversationModal;

const conversationModalStyles = xcss({
    width: '100vw',
    height: '100vh',
    position: 'fixed',
    top: '0px',
    left: '0px',
});

const conversationBoxStyles = xcss({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',
    height: '100%',
    top: token('space.1000', '80px'),
    backgroundColor: 'elevation.surface.overlay',
    paddingTop: token('space.300', '24px'),
    paddingBottom: token('space.200', '16px'),
    overflow: 'auto',
});
